import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {Sked} from 'src/app/current-sked/shared/model/sked';
import {TaskStatusActions} from 'src/app/current-sked/shared/task-status-actions';
import {UpdateSubtaskMetric} from 'src/app/current-sked/task-detail/current-sked-task-detail.component';
import {environment} from 'src/environments/environment';
import {Alert} from '../model/alert';
import {CSChallengeCycle, CSChallengeData, CSChartDetail, MetricUpdate} from '../model/project';
import {CurrentSkedTask, SubtaskNote} from '../model/task';

@Injectable({
  providedIn: 'root',
})
export class CurrentSkedApiService {
  constructor(private http: HttpClient) {}

  private get url() {
    return environment.apiUrl;
  }

  get(expired: boolean): Observable<{teamSkedWithTasks: Sked; userSkedWithTasks: Sked}> {
    return this.http.get<{teamSkedWithTasks: Sked; userSkedWithTasks: Sked}>(
      `${this.url}/execution/user?expired=${String(expired)}`
    );
  }

  getProject(projectId: string, expired: boolean): Observable<Sked> {
    return this.http.get<Sked>(`${this.url}/execution/project/${projectId}?expired=${String(expired)}`);
  }

  getAllSkeds(): Observable<Sked[]> {
    return this.http.get<Sked[]>(`${this.url}/execution/day`);
  }

  getCurrentSkedTask(taskId: string): Observable<CurrentSkedTask> {
    return this.http.get<CurrentSkedTask>(`${this.url}/execution/task/${taskId}`);
  }

  changeTaskStatus(taskId: string, type: TaskStatusActions): Observable<CurrentSkedTask> {
    return this.http.patch<CurrentSkedTask>(`${this.url}/execution/${type}/${taskId}`, {});
  }

  completeTask(taskId: string): Observable<{task: CurrentSkedTask; activities: Alert[]}> {
    return this.http.patch<{task: CurrentSkedTask; activities: Alert[]}>(
      `${this.url}/execution/complete/${taskId}`,
      {}
    );
  }

  getChallenges(projectId: string, expired: boolean): Observable<Sked> {
    return this.http.get<Sked>(`${this.url}/execution/challenges/${projectId}?expired=${String(expired)}`);
  }

  getCurrentSkedChallenge(challengeId: string): Observable<CSChallengeData> {
    return this.http.get<CSChallengeData>(`${this.url}/execution/challenge/${challengeId}/day`);
  }

  getChallengeCycleDay(challengeId: string, timestamp: string): Observable<CSChallengeData> {
    return this.http.get<CSChallengeData>(`${this.url}/execution/challenge/${challengeId}/day?timestamp=${timestamp}`);
  }

  getChallengeDayDetail(projectId: string, timestamp: string, metricId: string): Observable<CSChartDetail> {
    return this.http.get<CSChartDetail>(
      `${this.url}/execution/challenge/${projectId}/day/detail?timestamp=${encodeURIComponent(
        timestamp
      )}&metricId=${encodeURIComponent(metricId)}`
    );
  }

  getChallengeCycleByDate(projectId: string, date: string): Observable<CSChallengeCycle> {
    return this.http.get<CSChallengeCycle>(`${this.url}/cycles/byDate/${projectId}/${date}`);
  }

  saveChallengeMetricValues(projectId: string, updates: MetricUpdate[]): Observable<CSChallengeData> {
    return this.http.patch<CSChallengeData>(`${this.url}/execution/challenge/${projectId}/metrics`, {
      metricUpdates: updates,
    });
  }

  changeSubTaskStatus(
    taskId: string,
    subtaskId: string,
    complete: boolean
  ): Observable<{task: CurrentSkedTask; activities: Alert[]}> {
    const payload = {
      taskId,
      subtaskId,
      complete,
    };
    return this.http.patch<{task: CurrentSkedTask; activities: Alert[]}>(`${this.url}/execution/subtask`, payload);
  }

  updateSubtaskMetricValue(
    trackerId: string,
    taskId: string,
    metrics: UpdateSubtaskMetric[],
    subtaskId: string,
    timestamp?: string,
    taskExpired?: boolean
  ): Observable<{task: CurrentSkedTask; activities: Alert[]}> {
    const payload = {
      trackerId,
      taskId,
      metrics,
      subtaskId,
      timestamp,
      expired: taskExpired,
    };

    if (!timestamp) {
      delete payload.timestamp;
    }

    return this.http.patch<{task: CurrentSkedTask; activities: Alert[]}>(
      `${this.url}/execution/subtask/tracker`,
      payload
    );
  }

  updateCurrentSked(sked: Sked): Observable<Sked> {
    return this.http.put<Sked>(`${this.url}/skeds/${sked._id}`, sked);
  }

  addSubtaskNote(subtaskNote: SubtaskNote): Observable<CurrentSkedTask> {
    return this.http.patch<CurrentSkedTask>(`${this.url}/execution/subtask/note/add`, subtaskNote);
  }
}
